import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import updateUserPreferences from '../../components/api/mutations/updateUserPreferences';
import useUserQuery from '../../components/api/queries/useUserQuery';
import { FEATURE_KEYS } from '../../domains/features/constants';
import FeatureGate from '../../domains/features/ui/components/FeatureGate';
import { ReactComponent as SettingIcon } from '../../icons/setting.svg';
import HomeBottomNav from '../../layout/HomeBottomNav';
import PopUpBasic from '../components/popup/PopUpBasic';
import { useSuccessPhotoCardToast } from '../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import AddLikedArtistButton from './components/AddLikedArtistButton';
import HelpCard from './components/HelpCard';
import MyFanDomList from './components/MyFanDomList';
import MyPageHeader from './components/MyPageHeader';
import MyPageSubTitle from './components/MyPageSubTitle';
import BettieProfileCard from './components/BettieProfileCard';
import SocialProfileCard from './components/SocialProfileCard';
import TradingProfileCard from './components/TradingProfileCard';
import GradientCartIcon from './my-page-test-icon/GradientCartIcon';
import GradientHeartIcon from './my-page-test-icon/GradientHeartIcon';
import GradientPeopleIcon from './my-page-test-icon/GradientPeopleIcon';
import HeadsetIcon from './my-page-test-icon/HeadsetIcon';
import { ReactComponent as StarShapeFillIcon } from '../../icons/star.shape.fill.svg';
import useChatRoomsQuery from '../../components/api/queries/useChatRoomsQuery';

const subTitleGroup = {
  bettie: {
    title: 'myPage.header.bettie',
    description: 'myPage.description.bettie',
    icon: <StarShapeFillIcon width={18} height={18} />
  },
  socialProfile: {
    title: 'myPage.header.socialProfile',
    description: 'myPage.description.socialProfile',
    icon: <GradientPeopleIcon />
  },
  tradingProfile: {
    title: 'myPage.header.tradingProfile',
    description: 'myPage.description.tradingProfile',
    icon: <GradientCartIcon />
  },
  help: {
    title: 'myPage.header.help',
    icon: <HeadsetIcon />
  },
  fanDoms: {
    title: 'myPage.header.fandom',
    icon: <GradientHeartIcon />
  }
};

const NewMyPage = () => {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();

  const user = useUserQuery();
    const { data, isFetching: isChatRoomsLoading } = useChatRoomsQuery(
      {
        type: 'All',
        tradingPhotoCardId: undefined,
        onlyJoined: true,
        onlyBettie: true
      },
      {
        refetchOnWindowFocus: false
      }
    );

  const queryClient = useQueryClient();
  const showSuccessToast = useSuccessPhotoCardToast();

  const [isShowTradingProfileBottomSheet, setIsShowTradingProfileBottomSheet] = useState(false);
  const [showTradingProfile, setShowTradingProfile] = useState(user?.showTradingProfile || false);

  const onToggleShowTradingProfile = async () => {
    try {
      const response = await updateUserPreferences({
        showTradingProfile: !user.showTradingProfile
      });
      if (response.ok) {
        queryClient.invalidateQueries(['user']);
        setIsShowTradingProfileBottomSheet(false);
        setShowTradingProfile(!showTradingProfile);
        if (showTradingProfile) {
          showSuccessToast('top', t('myPage.hideTradingProfileSuccessMessage') as string);
        } else {
          showSuccessToast('top', t('myPage.showTradingProfileSuccessMessage') as string);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const bettieChatRooms = data?.pages?.flatMap((page) => page.chatRooms) ?? [];

  return (
    <div className="bg-background pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+100px)] px-[24px]">
      <MyPageHeader />

      {/* 내가 활동중인 팬덤 */}
      <MyPageSubTitleSection marginTop={10}>
        <div className="flex items-center justify-between">
          <MyPageSubTitle icon={subTitleGroup.fanDoms.icon} title={subTitleGroup.fanDoms.title} />
          <AddLikedArtistButton theme={colorMode} />
        </div>
        <MyFanDomList />
      </MyPageSubTitleSection>

      {/* 베티 메뉴 */}
      <MyPageSubTitleSection>
        <MyPageSubTitle
          icon={subTitleGroup.bettie.icon}
          title={subTitleGroup.bettie.title}
          description={subTitleGroup.bettie.description}
        />
        {!isChatRoomsLoading && <BettieProfileCard bettieChatRooms={bettieChatRooms} />}
      </MyPageSubTitleSection>

      {/* 소셜 프로필 메뉴 */}
      <MyPageSubTitleSection>
        <MyPageSubTitle
          icon={subTitleGroup.socialProfile.icon}
          title={subTitleGroup.socialProfile.title}
          description={subTitleGroup.socialProfile.description}
        />
        {user && <SocialProfileCard user={user} />}
      </MyPageSubTitleSection>

      {/* 트레이딩 프로필 메뉴 */}
      <MyPageSubTitleSection>
        <MyPageSubTitle
          icon={subTitleGroup.tradingProfile.icon}
          title={subTitleGroup.tradingProfile.title}
          description={subTitleGroup.tradingProfile.description}
        />
        {user && (
          <TradingProfileCard
            user={user}
            showTradingProfile={showTradingProfile}
            setIsShowTradingProfileBottomSheet={setIsShowTradingProfileBottomSheet}
          />
        )}
      </MyPageSubTitleSection>

      <FeatureGate featureKey={FEATURE_KEYS.ADMIN}>
        <div className="flex flex-col items-start gap-5 mt-[26px]">
          <h3 className="text-main-light-3 dark:text-main-dark-3 text-sm font-semibold">
            어드민 기능
          </h3>
          <button
            type="button"
            className="flex items-center gap-[13px] cursor-pointer bg-transparent border-none p-0 w-full text-left"
            onClick={() => navigate('/admin/reported-photo-cards/confirm')}
          >
            <div
              className={
                'flex items-center justify-center bg-secondary-light dark:bg-secondary-dark rounded-full w-9 h-9'
              }
            >
              <SettingIcon
                className={'fill-onSecondary-light dark:fill-onSecondary-dark w-5 h-5'}
              />
            </div>
            <div
              className={
                'text-[15px] font-medium leading-[18px] tracking-[0em] text-left text-main-light-2 dark:text-main-dark-2'
              }
            >
              포토카드 관리
            </div>
          </button>
        </div>
      </FeatureGate>

      {/* 도움이 필요하신가요? 메뉴 */}
      <MyPageSubTitleSection>
        <MyPageSubTitle icon={subTitleGroup.help.icon} title={subTitleGroup.help.title} />
        <HelpCard locale={user?.locale} />
      </MyPageSubTitleSection>

      <div className="mt-[40px]" />
      <Footer locale={user?.locale} />
      {isShowTradingProfileBottomSheet && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-[1000] flex items-center justify-center">
          <PopUpBasic
            type="large"
            title={
              showTradingProfile
                ? t('myPage.hideTradingProfilePopupTitle')
                : t('myPage.showTradingProfilePopupTitle')
            }
            description={
              showTradingProfile
                ? t('myPage.hideTradingProfilePopupDescription')
                : t('myPage.showTradingProfilePopupDescription')
            }
            primaryButtonText={
              showTradingProfile
                ? t('myPage.hideTradingProfilePopupAcceptButton')
                : t('myPage.showTradingProfilePopupAcceptButton')
            }
            secondaryButtonText={t('myPage.showTradingProfilePopupRejectButton')}
            onPrimaryButtonClick={onToggleShowTradingProfile}
            onSecondaryButtonClick={() => setIsShowTradingProfileBottomSheet(false)}
            className="fixed bottom-[calc(66px+env(safe-area-inset-bottom))] w-[calc(100%-48px)] h-[284px] !min-h-fit"
          />
        </div>
      )}
      <div className="-mx-[24px]">
        <HomeBottomNav tabIndex={4} />
      </div>
    </div>
  );
};

const MyPageSubTitleSection = ({
  children,
  marginTop = 28
}: {
  children: React.ReactNode;
  marginTop?: number;
}) => {
  return (
    <section className="flex flex-col gap-[10px]" style={{ marginTop: `${marginTop}px` }}>
      {children}
    </section>
  );
};

const Footer = ({ locale }: { locale: string }) => {
  return (
    <footer className="flex flex-col text-[11px] text-sub-light-1 dark:text-sub-dark-1 gap-[8px] mb-[40px]">
      <a href={`https://fanhearts.com/terms/?tab=terms_of_service&locale=${locale}`}>
        {t('userProfilePage.terms')}
      </a>
      <a href={`https://fanhearts.com/terms/?tab=privacy_policy&locale=${locale}`}>
        {t('userProfilePage.privacy')}
      </a>
      <div className="mt-[16px]">
        <a href="https://www.fanhearts.com">@Fanhearts</a> inc. All rights reserved.
      </div>
    </footer>
  );
};

export default NewMyPage;
